import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * The response body's Preview table.
 *
 * Lifted out of ResponseViewer when it gained recursion — the viewer already
 * owns four tabs, three view modes and a sandboxed HTML frame, and a tree with
 * its own expansion state does not belong in the same file.
 *
 * WHAT CHANGED, and why it matters more than it looks: a nested object used to
 * render as `JSON.stringify(value)` in one clipped cell, with the full text on
 * a `title` tooltip. For the shape almost every real API returns — a list whose
 * rows carry nested objects, or an envelope like `{ data: { items: [...] } }` —
 * the "preview" showed a wall of braces and the actual content was reachable
 * only by switching to the Pretty view. Postman's table preview drills in, and
 * that is the behaviour people arrive expecting.
 *
 * Expansion state is keyed by PATH, not by object identity or row index, so
 * re-rendering the same response — a re-run, a tab switch, a theme change —
 * keeps whatever the user had open. Identity would break on every re-parse;
 * index would reopen the wrong row after a sort.
 *
 * Segments are joined with U+0000 rather than a dot, because JSON keys may
 * CONTAIN dots: `{"a.b": 1, "a": {"b": 2}}` gives both `a.b` at the root and
 * `b` inside `a`, and a dotted path makes those the same string — expanding
 * one silently toggles the other. A NUL cannot appear in a key that came from
 * `JSON.parse`, so the join is unambiguous.
 */

const SEP = '\u0000';
const childPath = (path, key) => path + SEP + key;

const EXPANDABLE = (v) => v !== null && typeof v === 'object';

/** Objects and arrays get a summary; everything else renders as its own value. */
function summarize(value) {
  if (Array.isArray(value)) {
    return value.length === 1 ? '1 item' : `${value.length} items`;
  }
  const keys = Object.keys(value);
  return keys.length === 1 ? '1 key' : `${keys.length} keys`;
}

export function CellValue({ value }) {
  if (value === null) return <span className="rv-cell-null">null</span>;
  if (value === undefined) return <span className="rv-cell-null">—</span>;
  if (typeof value === 'boolean') return <span className="rv-cell-bool">{String(value)}</span>;
  if (typeof value === 'number') return <span className="rv-cell-num">{value}</span>;
  if (typeof value === 'string') return <span className="rv-cell-str">{value}</span>;
  // Object or array reached through a path that cannot expand (a cell inside
  // the spreadsheet view). Compact JSON with the full text on hover, as before.
  return (
    <span className="rv-cell-obj" title={JSON.stringify(value, null, 2)}>
      {JSON.stringify(value)}
    </span>
  );
}

/**
 * One key/value row, plus its children when open.
 *
 * Rendered as sibling `<tr>`s rather than a nested `<table>` inside a cell:
 * one table means one set of column widths, so a child's key column lines up
 * with its parent's instead of every level inventing its own grid. Depth is
 * carried by left padding on the key cell.
 */
function Row({ path, label, value, depth, open, onToggle }) {
  const expandable = EXPANDABLE(value);
  const isOpen = expandable && open.has(path);
  const entries = isOpen
    ? Array.isArray(value)
      ? value.map((v, i) => [String(i), v])
      : Object.entries(value)
    : [];

  return (
    <>
      <tr className={`rv-json-tr${expandable ? ' rv-json-tr--parent' : ''}`}>
        <td
          className="rv-json-td rv-json-td--key"
          style={{ paddingLeft: 12 + depth * 16 }}
        >
          {expandable ? (
            <button
              type="button"
              className="rv-json-expander"
              onClick={() => onToggle(path)}
              aria-expanded={isOpen}
              // The label says what it opens, not "expand row" — a screen
              // reader user moving down a deep tree otherwise hears the same
              // two words forty times.
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${label}`}
            >
              <ChevronRight size={12} className={isOpen ? 'rv-json-chev rv-json-chev--open' : 'rv-json-chev'} />
            </button>
          ) : (
            <span className="rv-json-expander rv-json-expander--leaf" aria-hidden="true" />
          )}
          <span className="rv-json-key-text">{label}</span>
        </td>
        <td className="rv-json-td">
          {expandable ? (
            <button type="button" className="rv-json-summary" onClick={() => onToggle(path)}>
              {Array.isArray(value) ? '[' : '{'}
              <span className="rv-json-summary-count">{summarize(value)}</span>
              {Array.isArray(value) ? ']' : '}'}
            </button>
          ) : (
            <CellValue value={value} />
          )}
        </td>
      </tr>
      {entries.map(([key, child]) => (
        <Row
          key={childPath(path, key)}
          path={childPath(path, key)}
          label={key}
          value={child}
          depth={depth + 1}
          open={open}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}

/**
 * The preview.
 *
 * Two shapes, chosen by the data rather than by a toggle:
 *
 * * an array whose entries are objects renders as a SPREADSHEET — one column
 *   per key, which is the view that makes a list of records readable at a
 *   glance, and the reason this component did not simply become a tree. Each
 *   row still expands, into a full key/value tree for that record, so a nested
 *   field is one click away instead of unreachable.
 * * everything else renders as the key/value TREE.
 */
export default function JsonPreviewTable({ data }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (path) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const isRecordList =
    Array.isArray(data) &&
    data.length > 0 &&
    data.some((r) => r !== null && typeof r === 'object' && !Array.isArray(r));

  const columns = useMemo(() => {
    if (!isRecordList) return [];
    return [...new Set(data.flatMap((row) => (row && typeof row === 'object' ? Object.keys(row) : [])))];
  }, [data, isRecordList]);

  if (isRecordList) {
    return (
      <div className="rv-json-table-wrap">
        <table className="rv-json-table">
          <thead>
            <tr>
              <th className="rv-json-th rv-json-th--idx">#</th>
              {columns.map((k) => (
                <th key={k} className="rv-json-th">
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const path = childPath('root', i);
              const nested = row !== null && typeof row === 'object';
              const isOpen = nested && open.has(path);
              return (
                <React.Fragment key={i}>
                  <tr className={`rv-json-tr${nested ? ' rv-json-tr--parent' : ''}`}>
                    <td className="rv-json-td rv-json-td--idx">
                      {nested ? (
                        <button
                          type="button"
                          className="rv-json-expander"
                          onClick={() => toggle(path)}
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Collapse' : 'Expand'} row ${i}`}
                        >
                          <ChevronRight
                            size={12}
                            className={isOpen ? 'rv-json-chev rv-json-chev--open' : 'rv-json-chev'}
                          />
                        </button>
                      ) : null}
                      {i}
                    </td>
                    {columns.map((k) => (
                      <td key={k} className="rv-json-td">
                        <CellValue value={row?.[k]} />
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    // The expanded record spans every column so its own key
                    // column is not squeezed into the width of column 0.
                    <tr className="rv-json-tr rv-json-tr--nested">
                      <td className="rv-json-td rv-json-td--nest" colSpan={columns.length + 1}>
                        <table className="rv-json-table rv-json-table--inner">
                          <tbody>
                            {Object.entries(row).map(([k, v]) => (
                              <Row
                                key={childPath(path, k)}
                                path={childPath(path, k)}
                                label={k}
                                value={v}
                                depth={0}
                                open={open}
                                onToggle={toggle}
                              />
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (data !== null && typeof data === 'object') {
    const entries = Array.isArray(data)
      ? data.map((v, i) => [String(i), v])
      : Object.entries(data);

    return (
      <div className="rv-json-table-wrap">
        <table className="rv-json-table">
          <thead>
            <tr>
              {/* An array is indexed, not keyed — the header says so, the way
                  it did before this view became recursive. */}
              <th className={`rv-json-th${Array.isArray(data) ? ' rv-json-th--idx' : ''}`}>
                {Array.isArray(data) ? '#' : 'key'}
              </th>
              <th className="rv-json-th">value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([k, v]) => (
              <Row
                key={childPath('root', k)}
                path={childPath('root', k)}
                label={k}
                value={v}
                depth={0}
                open={open}
                onToggle={toggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="rv-json-scalar">
      <CellValue value={data} />
    </div>
  );
}
